/**
 * Secondary presentation axes (Phase 4).
 *
 * These compose with whichever layout an owner picked: any post style works
 * with any layout, and density applies to the whole shell.
 *
 * `nav` is how the navigation COLUMN is presented — icon-only or labelled —
 * NOT whether a switcher rail sits beside it. That second thing is the Spaces
 * layout (lib/layouts.ts), and the two compose rather than duplicate:
 *
 *   Standard + nav "rail"    switcher rail, then an icon-only nav column
 *   Standard + nav "sidebar" switcher rail, then a labelled nav column
 *   Spaces   + nav "rail"    one icon-only column, no switcher rail
 *   Spaces   + nav "sidebar" one labelled column, no switcher rail
 *
 * Reader has no nav column at all (its navigation is the top bar), so `nav`
 * has no effect there.
 *
 * Keep in sync with VALID_NAV_MODES / VALID_POST_STYLES / VALID_DENSITIES in
 * truman/src/lambda/api/studio-trpc/routers/studio.ts.
 */

export type NavMode = "sidebar" | "rail";
export type PostStyle = "cards" | "grid" | "rows";
export type Density = "comfortable" | "compact";

/**
 * Today's behaviour. A studio that never touches these settings, or whose
 * stored value cannot be read, renders exactly as it does now.
 */
export const FALLBACK_NAV_MODE: NavMode = "sidebar";
export const FALLBACK_POST_STYLE: PostStyle = "cards";
export const FALLBACK_DENSITY: Density = "comfortable";

export interface AxisOption<T extends string> {
  id: T;
  name: string;
  description: string;
}

// `satisfies` rather than an annotation — an annotation widens each `id` back
// to the union and defeats the exhaustiveness guards in the tests.
export const NAV_OPTIONS = [
  {
    id: "sidebar",
    name: "Labels",
    description:
      "A full column with names beside each icon. The current design.",
  },
  {
    id: "rail",
    name: "Icons only",
    description:
      "A slim column of icons. Members can still expand it for themselves.",
  },
] satisfies readonly AxisOption<NavMode>[];

export const POST_STYLE_OPTIONS = [
  {
    id: "cards",
    name: "Cards",
    description: "Full-width posts with the media below the text.",
  },
  {
    id: "grid",
    name: "Grid",
    description: "Posts side by side in a grid. Suits visual studios.",
  },
  {
    id: "rows",
    name: "Rows",
    description: "Compact rows. Fits far more on screen.",
  },
] satisfies readonly AxisOption<PostStyle>[];

export const DENSITY_OPTIONS = [
  {
    id: "comfortable",
    name: "Comfortable",
    description: "Roomy spacing. The current design.",
  },
  {
    id: "compact",
    name: "Compact",
    description: "Tighter spacing throughout, so more fits on screen.",
  },
] satisfies readonly AxisOption<Density>[];

/**
 * Anything unknown, null, or from a rolled-back value set resolves to today's.
 *
 * `nav` sets the studio-wide DEFAULT only. A member who has collapsed or
 * expanded the sidebar themselves keeps that — see use-sidebar-collapse.ts,
 * where the stored per-browser value takes precedence over this.
 */
export const toNavMode = (value: unknown): NavMode =>
  NAV_OPTIONS.some((option) => option.id === value)
    ? (value as NavMode)
    : FALLBACK_NAV_MODE;

export const toPostStyle = (value: unknown): PostStyle =>
  POST_STYLE_OPTIONS.some((option) => option.id === value)
    ? (value as PostStyle)
    : FALLBACK_POST_STYLE;

export const toDensity = (value: unknown): Density =>
  DENSITY_OPTIONS.some((option) => option.id === value)
    ? (value as Density)
    : FALLBACK_DENSITY;

/**
 * Why the `nav` axis does nothing under a given layout, or null if it applies.
 *
 * Reader's navigation is the top bar — there is no column to present as icons
 * or labels — so the setting is inert there. The settings page uses this to
 * disable the control and say why, rather than letting an owner pick a value,
 * save it, and see nothing change.
 *
 * Lives here rather than in the component so the layout that ignores `nav` and
 * the control that offers it read the same rule. If a future layout also drops
 * its nav column, add it here and both sides follow.
 */
export function navDisabledReason(layoutId: string): string | null {
  if (layoutId === "reader") {
    return "Reader puts navigation in the top bar, so there is no column to show as icons or labels. Pick another layout to change this.";
  }
  return null;
}
